/**
 * DriverOverviewTab - Driver overview information with accordions
 *
 * Displays (ALL ACCORDIONS CLOSED BY DEFAULT):
 * - Driver info (name, email, organization, status, address)
 * - License & Certifications (categories A-CE, professional certs)
 * - Assigned Vehicles list
 * - Quick actions for vehicle assignment and org transfer
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building, Car, ClipboardCheck, Plus, User, CreditCard, Award, MapPin, Edit2,
  UserCheck, UserMinus, UserX, HelpCircle, Calendar, Trash2, Briefcase, CircleDot,
  ParkingCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../../i18n/format.js';
import api from '../../../api.js';
import { fuelCardAPI, driverBadgeAPI } from '../../../api/index.js';
import { handleApiError } from '../../../utils/errorHandler.js';
import { AccordionGroup, AccordionContainer } from '../../common/AccordionGroup.jsx';
import VehicleAssignmentToDriverModal from '../modals/VehicleAssignmentToDriverModal.jsx';
import DriverOrgTransferModal from '../modals/DriverOrgTransferModal.jsx';
import DriverSiteAssignmentModal from '../modals/DriverSiteAssignmentModal.jsx';
import DriverAvailabilityModal from '../modals/DriverAvailabilityModal.jsx';
import DriverEquipmentAssignModal from '../modals/DriverEquipmentAssignModal.jsx';
import MissingEmailDot from '../badges/MissingEmailDot.jsx';
import PoolPresenceCard from '../../pool/PoolPresenceCard.jsx';
import { isPlaceholderEmail, textToCategories, booleansToCategories } from '../../../utils/driverLicense.js';

// Availability status configuration — `label` = repli FR, l'affichage passe par t('drivers.availability.status.<code>')
const AVAILABILITY_CONFIG = {
  AVAILABLE: { icon: UserCheck, color: 'green', label: 'Disponible', bgColor: 'bg-green-100', textColor: 'text-green-800' },
  UNAVAILABLE_PLANNED: { icon: UserMinus, color: 'yellow', label: 'Indisponible (prévu)', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
  UNAVAILABLE_UNPLANNED: { icon: UserX, color: 'red', label: 'Indisponible (imprévu)', bgColor: 'bg-red-100', textColor: 'text-red-800' },
  UNKNOWN: { icon: HelpCircle, color: 'gray', label: 'Inconnu', bgColor: 'bg-gray-100', textColor: 'text-gray-600' }
};

const AVAILABILITY_REASON_LABELS = {
  NONE: null,
  LEAVE: 'Congé',
  SICK_LEAVE: 'Arrêt maladie',
  TRAINING: 'Formation',
  MEDICAL_VISIT: 'Visite médicale',
  ADMIN: 'Administratif',
  VEHICLE_ISSUE: 'Problème véhicule',
  OTHER: 'Autre'
};

// Status configuration — `label` = repli FR, l'affichage passe par t('drivers.status.<code>')
const STATUS_CONFIG = {
  active: { color: 'bg-green-100 text-green-800', label: 'CDI' },
  temporary: { color: 'bg-amber-100 text-amber-800', label: 'CDD' },
  interim: { color: 'bg-blue-100 text-blue-800', label: 'Intérim' },
  inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactif' }
};

// Site type labels — repli FR, l'affichage passe par t('drivers.detail.siteTypes.<code>')
const SITE_TYPE_LABELS = {
  headquarters: 'Siege',
  branch: 'Agence',
  depot: 'Depot',
  parking: 'Parking',
  workshop: 'Atelier',
  client_site: 'Client',
  temporary: 'Temporaire',
  siege: 'Siege',
  agence: 'Agence',
  station: 'Station Service',
  chantier: 'Chantier',
  autre: 'Autre'
};

// License category badge component
function LicenseBadge({ label, isValid }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
        isValid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
      }`}>
        {label}
      </span>
      <span className="text-xs text-gray-600">
        {isValid ? `✓ ${t('drivers.detail.license.valid')}` : `✗ ${t('drivers.detail.license.notHeld')}`}
      </span>
    </div>
  );
}

// Certification badge component
function CertBadge({ label, isValid }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
        isValid ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'
      }`}>
        {label}
      </span>
      <span className="text-xs text-gray-600">
        {isValid ? `✓ ${t('drivers.detail.license.held')}` : `✗ ${t('drivers.detail.license.notHeld')}`}
      </span>
    </div>
  );
}

// Equipment provider labels
const FUEL_CARD_PROVIDERS = {
  totalenergies: 'TotalEnergies', dkv: 'DKV', shell: 'Shell',
  uta_edenred: 'UTA/Edenred', esso_wex: 'Esso/WEX', leclerc_pro: 'Leclerc Pro', other: 'Autre'
};
const BADGE_PROVIDERS = {
  vinci: 'Vinci Autoroutes', aprr: 'APRR', sanef: 'Sanef', area: 'AREA',
  bipandgo: "Bip&Go", ulys: 'Ulys', indigo: 'Indigo', qpark: 'Q-Park',
  effia: 'Effia', saemes: 'Saemes', zenpark: 'Zenpark', site_badge: 'Badge site', other: 'Autre'
};

const EQUIPMENT_STATUS = {
  active: { color: 'bg-green-100 text-green-800', label: 'Actif' },
  blocked: { color: 'bg-red-100 text-red-800', label: 'Bloqué' },
  expired: { color: 'bg-gray-100 text-gray-600', label: 'Expiré' },
  lost: { color: 'bg-orange-100 text-orange-800', label: 'Perdu' },
  cancelled: { color: 'bg-gray-100 text-gray-500', label: 'Annulé' },
};

function ConsumptionBar({ consumed, limit, pct }) {
  const { t, fmt } = useFormat();
  // No consumption at all — hide
  if (!consumed && (!limit || limit <= 0)) return null;

  const hasLimit = limit && limit > 0;

  // No limit set but has consumption — show flat amount
  if (!hasLimit) {
    return (
      <div className="mt-2">
        <span className="text-xs font-semibold text-gray-700">
          {t('drivers.detail.equipment.consumedThisMonth', { amount: fmt.money(consumed, 'EUR', { decimals: 0 }) })}
        </span>
      </div>
    );
  }

  // Has limit — show progress bar
  const displayPct = Math.min(pct ?? 0, 100);
  const barColor = displayPct >= 90 ? 'bg-red-500' : displayPct >= 70 ? 'bg-orange-400' : displayPct >= 50 ? 'bg-yellow-400' : 'bg-emerald-500';
  const textColor = displayPct >= 90 ? 'text-red-700' : displayPct >= 70 ? 'text-orange-700' : 'text-gray-700';

  return (
    <div className="mt-2">
      <div className="flex justify-between items-baseline mb-1">
        <span className={`text-xs font-semibold ${textColor}`}>
          {fmt.money(consumed, 'EUR', { decimals: 0 })} / {fmt.money(limit, 'EUR', { decimals: 0 })}
        </span>
        <span className={`text-xs font-medium ${textColor}`}>{displayPct}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${displayPct}%` }} />
      </div>
      {displayPct >= 90 && (
        <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {t('drivers.detail.equipment.limitAlmostReached')}
        </p>
      )}
    </div>
  );
}

function EquipmentCard({ item, type, onUnassign, consumption }) {
  const { t, fmt } = useFormat();
  const isFuelCard = type === 'fuel_card';
  const providerLabel = isFuelCard
    ? (FUEL_CARD_PROVIDERS[item.provider] || item.provider_name || item.provider)
    : (BADGE_PROVIDERS[item.provider] || item.provider_name || item.provider);
  const number = isFuelCard ? item.card_number : item.badge_number;
  const status = EQUIPMENT_STATUS[item.status] || EQUIPMENT_STATUS.active;
  const isExpiring = item.expiry_date && new Date(item.expiry_date) < new Date(Date.now() + 30 * 86400000);
  const isExpired = item.expiry_date && new Date(item.expiry_date) < new Date();

  const iconMap = { fuel_card: CreditCard, toll: CircleDot, parking: ParkingCircle };
  const colorMap = { fuel_card: 'border-blue-200', toll: 'border-amber-200', parking: 'border-purple-200' };
  const typeLabel = { fuel_card: t('drivers.equipment.fuelCard'), toll: t('drivers.equipment.tollBadge'), parking: t('drivers.equipment.parkingBadge') };
  const TypeIcon = iconMap[type] || CreditCard;

  return (
    <div className={`bg-white rounded-lg p-3 border ${colorMap[type] || 'border-gray-200'}`}>
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <TypeIcon className="w-4 h-4 text-gray-500" />
          <div>
            <p className="font-medium text-gray-900 text-sm">{providerLabel}</p>
            <p className="text-xs text-gray-500 font-mono">{number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${status.color}`}>
            {t(`drivers.detail.equipment.status.${EQUIPMENT_STATUS[item.status] ? item.status : 'active'}`, { defaultValue: status.label })}
          </span>
          {onUnassign && (
            <button
              onClick={() => onUnassign(item, type)}
              className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
              title={t('drivers.detail.actions.unassign')}
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
        <span>{typeLabel[type]}</span>
        {item.expiry_date && (
          <span className={isExpired ? 'text-red-600 font-medium' : isExpiring ? 'text-orange-600 font-medium' : ''}>
            {isExpired && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
            {t('drivers.detail.equipment.expiryShort', { date: fmt.date(item.expiry_date) })}
          </span>
        )}
        {!consumption && item.monthly_limit && (
          <span>{t('drivers.detail.equipment.monthlyLimit', { amount: fmt.money(item.monthly_limit, 'EUR', { decimals: 0 }) })}</span>
        )}
      </div>
      {consumption && (
        <ConsumptionBar consumed={consumption.consumed} limit={consumption.monthly_limit} pct={consumption.consumed_pct} />
      )}
    </div>
  );
}

// Assigned vehicle card
function AssignedVehicleCard({ vehicle, trip, onUnassign }) {
  const { t, fmt } = useFormat();
  return (
    <div className="bg-white rounded-lg p-3 border border-green-200">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-medium text-gray-900">{vehicle.make} {vehicle.model}</p>
          <p className="text-sm text-gray-600">{vehicle.license_plate}</p>
          {trip.purpose && <p className="text-xs text-gray-500 mt-1">{t('drivers.detail.vehicles.purpose')}: {trip.purpose}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full ${
            trip.status === 'in_progress' ? 'bg-green-100 text-green-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {trip.status === 'in_progress' ? t('drivers.detail.vehicles.inProgress') : t('drivers.detail.vehicles.planned')}
          </span>
          {onUnassign && (
            <button
              onClick={() => onUnassign(trip, vehicle)}
              className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
              title={t('drivers.detail.vehicles.unassignTitle')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {t('drivers.detail.vehicles.start')}: {fmt.date(trip.start_date)}
        {trip.end_date && ` - ${t('drivers.detail.vehicles.end')}: ${fmt.date(trip.end_date)}`}
      </div>
    </div>
  );
}

export function DriverOverviewTab({ entity, entityType, onMessage, onUpdate }) {
  const { t, fmt } = useFormat();
  const driver = entity;

  // Related data state
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [sites, setSites] = useState([]);
  const [fuelCards, setFuelCards] = useState([]);
  const [driverBadges, setDriverBadges] = useState([]);
  const [fuelConsumption, setFuelConsumption] = useState({});
  const [badgeConsumption, setBadgeConsumption] = useState({});

  // Accordion states - ALL CLOSED BY DEFAULT
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());

  // Modal states
  const [showVehicleAssignment, setShowVehicleAssignment] = useState(false);
  const [showOrgTransfer, setShowOrgTransfer] = useState(false);
  const [showSiteAssignment, setShowSiteAssignment] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showEquipmentAssign, setShowEquipmentAssign] = useState(false);

  // Toggle accordion
  const toggleAccordion = (id) => {
    setExpandedAccordions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isExpanded = (id) => expandedAccordions.has(id);

  // Load related data
  const loadData = useCallback(async () => {
    if (!driver?.id) return;

    try {
      const [vehiclesRes, tripsRes, orgsRes, sitesRes, fuelCardsRes, badgesRes] = await Promise.all([
        api.vehicle.getAll(),
        api.trip.getAll(),
        api.organization.getAll(),
        api.workingSite.getAll(),
        fuelCardAPI.getAll({ driver_id: driver.id }),
        driverBadgeAPI.getAll({ driver_id: driver.id }),
      ]);

      setVehicles(vehiclesRes.data || []);
      setTrips((tripsRes.data || []).filter(t => t.driver_id === driver.id));
      setOrganizations(orgsRes.data || []);
      setSites(sitesRes.data || []);
      setFuelCards(fuelCardsRes.data || []);
      setDriverBadges(badgesRes.data || []);

      // Fetch consumption data (non-blocking — don't fail the whole load)
      try {
        const [fcConsRes, bdConsRes] = await Promise.all([
          fuelCardAPI.getConsumption(),
          driverBadgeAPI.getConsumption(),
        ]);
        // Index by card_id / badge_id for quick lookup
        const fcMap = {};
        (fcConsRes?.data || []).forEach(c => { fcMap[c.card_id] = c; });
        setFuelConsumption(fcMap);
        const bdMap = {};
        (bdConsRes?.data || []).forEach(c => { bdMap[c.badge_id] = c; });
        setBadgeConsumption(bdMap);
      } catch (_) { /* consumption is nice-to-have, don't break the page */ }
    } catch (error) {
      handleApiError(error, 'load', { onMessage, silent: !onMessage });
    }
  }, [driver?.id, onMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get assigned vehicles from active trips
  const getAssignedVehicles = useCallback(() => {
    const activeTrips = trips.filter(t =>
      t.driver_id === driver?.id && (t.status === 'in_progress' || t.status === 'planned')
    );
    return activeTrips.map(trip => {
      const vehicle = vehicles.find(v => v.id === trip.vehicle_id);
      return { vehicle, trip };
    }).filter(item => item.vehicle);
  }, [trips, vehicles, driver?.id]);

  // Handle assignment success
  const handleAssignmentSuccess = useCallback(() => {
    loadData();
    onUpdate?.();
  }, [loadData, onUpdate]);

  // Handle vehicle unassignment (end the trip)
  const handleUnassignVehicle = useCallback(async (trip, vehicle) => {
    const label = vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : t('drivers.detail.vehicles.thisVehicle');
    const confirmed = window.confirm(
      t('drivers.detail.vehicles.unassignConfirm', { label, name: `${driver?.first_name} ${driver?.last_name}` })
    );
    if (!confirmed) return;

    try {
      await api.trip.update(trip.id, {
        status: 'completed',
        end_date: new Date().toISOString().split('T')[0],
        end_mileage: vehicle?.mileage || trip.start_mileage || 0
      });

      onMessage?.({
        title: t('drivers.messages.successTitle'),
        message: t('drivers.detail.vehicles.unassignSuccess', { label }),
        type: 'success'
      });

      loadData();
      onUpdate?.();
    } catch (error) {
      handleApiError(error, 'unassign', { onMessage });
    }
  }, [driver, loadData, onUpdate, onMessage]);

  // Handle equipment unassign (remove driver_id from card/badge)
  const handleUnassignEquipment = useCallback(async (item, type) => {
    const label = type === 'fuel_card'
      ? t('drivers.detail.equipment.theCard', { number: item.card_number })
      : t('drivers.detail.equipment.theBadge', { number: item.badge_number });
    const confirmed = window.confirm(
      t('drivers.detail.equipment.unassignConfirm', { label, name: `${driver?.first_name} ${driver?.last_name}` })
    );
    if (!confirmed) return;

    try {
      if (type === 'fuel_card') {
        await fuelCardAPI.update(item.id, { driver_id: null, card_type: 'pool' });
      } else {
        await driverBadgeAPI.update(item.id, { driver_id: null, assignment_type: 'pool' });
      }
      onMessage?.({ title: t('drivers.messages.successTitle'), message: t('drivers.detail.equipment.unassignSuccess', { label }), type: 'success' });
      loadData();
      onUpdate?.();
    } catch (error) {
      handleApiError(error, 'unassign', { onMessage });
    }
  }, [driver, loadData, onUpdate, onMessage]);

  // Equipment counts
  const tollBadges = driverBadges.filter(b => b.badge_type === 'toll');
  const parkingBadges = driverBadges.filter(b => b.badge_type === 'parking');
  const equipmentCount = fuelCards.length + driverBadges.length;

  // Count valid licenses
  const validLicenseCount = [
    driver?.license_category_b,
    driver?.license_category_c,
    driver?.license_category_d,
    driver?.license_category_ce
  ].filter(Boolean).length;

  // Count certifications
  const certCount = [
    driver?.cert_adr,
    driver?.cert_fimo,
    driver?.cert_crane,
    driver?.cert_forklift
  ].filter(Boolean).length;

  const assignedVehicles = getAssignedVehicles();
  const statusConfig = STATUS_CONFIG[driver?.status] || STATUS_CONFIG.inactive;
  const statusLabel = t(`drivers.status.${STATUS_CONFIG[driver?.status] ? driver.status : 'inactive'}`, { defaultValue: statusConfig.label });

  // Memoized current site lookup with proper type handling
  const currentSite = useMemo(() => {
    if (!driver?.working_site_id || sites.length === 0) return null;
    const siteId = parseInt(driver.working_site_id);
    return sites.find(s => s.id === siteId) || null;
  }, [driver?.working_site_id, sites]);

  return (
    <div className="space-y-4">
      {/* « Où est cet agent, avec quel véhicule ? » — l'état COURANT, au-dessus
          des accordéons fermés. La garde (pool, quelques heures) n'est pas la
          dotation : celle-ci reste dans l'accordéon « Véhicules affectés ». */}
      <PoolPresenceCard
        kind="driver"
        id={driver?.id}
        subjectLabel={[driver?.first_name, driver?.last_name].filter(Boolean).join(' ') || null}
      />

      <AccordionContainer>
        {/* Driver Info Accordion */}
        <AccordionGroup
          id="driver-info"
          title={t('drivers.detail.info.title')}
          icon={User}
          color="blue"
          isExpanded={isExpanded('driver-info')}
          onToggle={() => toggleAccordion('driver-info')}
          summary={isPlaceholderEmail(driver?.email) ? t('drivers.missingEmail.label') : driver?.email}
          headerRight={
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.color}`}>
              {statusLabel}
            </span>
          }
        >
          <div className="space-y-4">
            {/* Basic Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.firstName')}</p>
                <p className="font-medium text-gray-900">{driver?.first_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.lastName')}</p>
                <p className="font-medium text-gray-900">{driver?.last_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.email')}</p>
                <p className="font-medium text-gray-900 text-sm flex items-center gap-1.5">
                  <MissingEmailDot email={driver?.email} />
                  {isPlaceholderEmail(driver?.email)
                    ? <span className="italic font-normal text-red-600">{t('drivers.missingEmail.label')}</span>
                    : (driver?.email || '-')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.phone')}</p>
                <p className="font-medium text-gray-900">{driver?.phone || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.birthDate')}</p>
                <p className="font-medium text-gray-900">{driver?.birth_date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.columns.organization')}</p>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  <p className="font-medium text-gray-900">{driver?.org_name || '-'}</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.address')}</p>
              <p className="font-medium text-gray-900">{driver?.address || t('drivers.licenseStatus.unknown')}</p>
            </div>

            {/* Quick Actions — dans la vraie vie un agent change de DIRECTION ou d'U.T. (un
                site de l'organigramme), pas d'organisation : sur un tenant mono-org le transfert
                d'organisation ne propose que l'org courante. Constat JGS en prod, 2026-09-15. */}
            <div className="flex justify-end gap-4 pt-3 border-t border-gray-100">
              <button
                onClick={() => setShowSiteAssignment(true)}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
              >
                <MapPin className="w-4 h-4" />
                {t('drivers.detail.actions.changeSite')}
              </button>
              {organizations.length > 1 && (
                <button
                  onClick={() => setShowOrgTransfer(true)}
                  className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1"
                >
                  <Building className="w-4 h-4" />
                  {t('drivers.detail.actions.transferOrg')}
                </button>
              )}
            </div>
          </div>
        </AccordionGroup>

        {/* Disponibilité Accordion */}
        {(() => {
          const availStatus = driver?.availability_status || 'UNKNOWN';
          const config = AVAILABILITY_CONFIG[availStatus] || AVAILABILITY_CONFIG.UNKNOWN;
          const AvailIcon = config.icon;
          const reasonLabel = AVAILABILITY_REASON_LABELS[driver?.availability_reason]
            ? t(`drivers.availability.reason.${driver.availability_reason}`, { defaultValue: AVAILABILITY_REASON_LABELS[driver.availability_reason] })
            : null;
          const availLabel = t(`drivers.availability.status.${AVAILABILITY_CONFIG[availStatus] ? availStatus : 'UNKNOWN'}`, { defaultValue: config.label });

          return (
            <AccordionGroup
              id="availability"
              title={t('drivers.columns.availability')}
              icon={AvailIcon}
              color={config.color}
              isExpanded={isExpanded('availability')}
              onToggle={() => toggleAccordion('availability')}
              summary={availLabel}
              headerRight={
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAvailability(true);
                  }}
                  className={`${config.textColor} hover:opacity-80 p-1 rounded hover:bg-opacity-50`}
                  title={t('drivers.actions.editAvailability')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              }
            >
              <div className="space-y-4">
                {/* Current Status */}
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full ${config.bgColor}`}>
                    <AvailIcon className={`w-6 h-6 ${config.textColor}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${config.textColor}`}>{availLabel}</p>
                    {reasonLabel && (
                      <p className="text-sm text-gray-600">{t('drivers.detail.availability.reason')}: {reasonLabel}</p>
                    )}
                  </div>
                </div>

                {/* Date Range if set */}
                {(driver?.availability_from || driver?.availability_until) && (
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    {driver?.availability_from && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {t('drivers.detail.vehicles.start')}
                        </p>
                        <p className="font-medium text-gray-900">
                          {fmt.date(driver.availability_from)}
                        </p>
                      </div>
                    )}
                    {driver?.availability_until && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {t('drivers.detail.availability.plannedEnd')}
                        </p>
                        <p className="font-medium text-gray-900">
                          {fmt.date(driver.availability_until)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Note if set */}
                {driver?.availability_note && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.availability.note')}</p>
                    <p className="text-sm text-gray-700 italic">"{driver.availability_note}"</p>
                  </div>
                )}

                {/* Quick Action */}
                <div className="flex justify-end pt-3 border-t border-gray-100">
                  <button
                    onClick={() => setShowAvailability(true)}
                    className={`${config.textColor} hover:opacity-80 text-sm font-medium flex items-center gap-1`}
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('drivers.actions.editAvailability')}
                  </button>
                </div>
              </div>
            </AccordionGroup>
          );
        })()}

        {/* Site de Travail Accordion */}
        <AccordionGroup
          id="working-site"
          title={t('drivers.detail.site.title')}
          icon={MapPin}
          color="purple"
          isExpanded={isExpanded('working-site')}
          onToggle={() => toggleAccordion('working-site')}
          summary={currentSite?.site_name || null}
          emptyMessage={t('drivers.detail.site.empty')}
          headerRight={
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSiteAssignment(true);
              }}
              className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-100"
              title={t('drivers.detail.site.editAssignment')}
            >
              <Edit2 className="w-4 h-4" />
            </button>
          }
        >
          {currentSite && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.site.name')}</p>
                  <p className="font-medium text-gray-900">{currentSite.site_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.site.type')}</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {t(`drivers.detail.siteTypes.${currentSite.site_type}`, { defaultValue: SITE_TYPE_LABELS[currentSite.site_type] || currentSite.site_type })}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.site.code')}</p>
                  <p className="font-medium text-gray-900">{currentSite.site_code || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.site.city')}</p>
                  <p className="font-medium text-gray-900">{currentSite.city || '-'}</p>
                </div>
              </div>
              {currentSite.address && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.info.address')}</p>
                  <p className="font-medium text-gray-900">{currentSite.address}</p>
                  {(currentSite.postal_code || currentSite.city) && (
                    <p className="text-sm text-gray-600">{currentSite.postal_code} {currentSite.city}</p>
                  )}
                </div>
              )}
              {currentSite.contact_name && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.site.contact')}</p>
                  <p className="font-medium text-gray-900">{currentSite.contact_name}</p>
                  {currentSite.contact_phone && (
                    <p className="text-sm text-gray-600">{currentSite.contact_phone}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </AccordionGroup>

        {/* License & Certifications Accordion */}
        <AccordionGroup
          id="license-certs"
          title={t('drivers.detail.license.title')}
          icon={ClipboardCheck}
          color="amber"
          count={validLicenseCount + certCount}
          isExpanded={isExpanded('license-certs')}
          onToggle={() => toggleAccordion('license-certs')}
          summary={driver?.license_number ? `${t('drivers.detail.license.licenseWord')}: ${driver.license_number}` : null}
        >
          <div className="space-y-4">
            {/* License Categories */}
            <div>
              <p className="text-sm text-gray-600 font-semibold mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-amber-600" />
                {t('drivers.detail.license.categories')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <LicenseBadge label={t('drivers.detail.license.category', { code: 'B' })} isValid={driver?.license_category_b} />
                <LicenseBadge label={t('drivers.detail.license.category', { code: 'C' })} isValid={driver?.license_category_c} />
                <LicenseBadge label={t('drivers.detail.license.category', { code: 'D' })} isValid={driver?.license_category_d} />
                <LicenseBadge label={t('drivers.detail.license.category', { code: 'CE' })} isValid={driver?.license_category_ce} />
              </div>

              {/* Catégories UE 2006/126/CE (au-delà des 4 badges historiques) :
                  le champ texte porte la liste normalisée complète. */}
              {(() => {
                const euCats = driver?.license_categories
                  ? textToCategories(driver.license_categories)
                  : booleansToCategories(driver || {});
                if (!euCats.length) return null;
                return (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-500 uppercase mr-1">{t('drivers.detail.license.euCategories')}</span>
                    {euCats.map(c => (
                      <span key={c} className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 text-amber-800">{c}</span>
                    ))}
                  </div>
                );
              })()}

              {driver?.license_country && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.license.country')}</p>
                  <p className="font-medium text-gray-900">{driver.license_country}</p>
                </div>
              )}
            </div>

            {/* Professional Certifications */}
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-600 font-semibold mb-2 flex items-center gap-2">
                <Award className="w-4 h-4 text-purple-600" />
                {t('drivers.detail.license.certifications')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <CertBadge label="ADR" isValid={driver?.cert_adr} />
                <CertBadge label="FIMO" isValid={driver?.cert_fimo} />
                <CertBadge label={t('drivers.detail.license.crane')} isValid={driver?.cert_crane} />
                <CertBadge label={t('drivers.detail.license.forklift')} isValid={driver?.cert_forklift} />
              </div>
            </div>

            {/* License Details */}
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.license.number')}</p>
                <p className="font-medium text-gray-900">{driver?.license_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.license.expiryDate')}</p>
                <p className="font-medium text-gray-900">{driver?.license_expiry || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.license.lastVerification')}</p>
                <p className="font-medium text-gray-900">{driver?.last_verification_date || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('drivers.detail.license.verificationFrequency')}</p>
                <p className="font-medium text-gray-900">{driver?.verification_frequency || t('drivers.detail.license.annual')}</p>
              </div>
            </div>
          </div>
        </AccordionGroup>

        {/* Assigned Vehicles Accordion */}
        <AccordionGroup
          id="assigned-vehicles"
          title={t('drivers.detail.vehicles.title')}
          icon={Car}
          color="emerald"
          count={assignedVehicles.length}
          isExpanded={isExpanded('assigned-vehicles')}
          onToggle={() => toggleAccordion('assigned-vehicles')}
          emptyMessage={t('drivers.detail.vehicles.empty')}
          headerRight={
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowVehicleAssignment(true);
              }}
              className="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-100"
              title={t('drivers.detail.vehicles.assign')}
            >
              <Plus className="w-5 h-5" />
            </button>
          }
        >
          {assignedVehicles.length > 0 && (
            <div className="space-y-3">
              {assignedVehicles.map(({ vehicle, trip }) => (
                <AssignedVehicleCard key={trip.id} vehicle={vehicle} trip={trip} onUnassign={handleUnassignVehicle} />
              ))}
            </div>
          )}
        </AccordionGroup>

        {/* Équipements Accordion (Fuel Cards + Toll + Parking) */}
        <AccordionGroup
          id="equipment"
          title={t('drivers.columns.equipment')}
          icon={Briefcase}
          color="blue"
          count={equipmentCount}
          isExpanded={isExpanded('equipment')}
          onToggle={() => toggleAccordion('equipment')}
          emptyMessage={t('drivers.detail.equipment.empty')}
          headerRight={
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowEquipmentAssign(true);
              }}
              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100"
              title={t('drivers.detail.equipment.assign')}
            >
              <Plus className="w-5 h-5" />
            </button>
          }
        >
          {equipmentCount > 0 && (
            <div className="space-y-3">
              {fuelCards.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1">
                    <CreditCard className="w-3 h-3" /> {t('drivers.detail.equipment.fuelCards')}
                  </p>
                  {fuelCards.map(card => (
                    <EquipmentCard key={`fc-${card.id}`} item={card} type="fuel_card" onUnassign={handleUnassignEquipment} consumption={fuelConsumption[card.id]} />
                  ))}
                </>
              )}
              {tollBadges.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1 mt-2">
                    <CircleDot className="w-3 h-3" /> {t('drivers.detail.equipment.tollBadges')}
                  </p>
                  {tollBadges.map(badge => (
                    <EquipmentCard key={`toll-${badge.id}`} item={badge} type="toll" onUnassign={handleUnassignEquipment} consumption={badgeConsumption[badge.id]} />
                  ))}
                </>
              )}
              {parkingBadges.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1 mt-2">
                    <ParkingCircle className="w-3 h-3" /> {t('drivers.detail.equipment.parkingBadges')}
                  </p>
                  {parkingBadges.map(badge => (
                    <EquipmentCard key={`park-${badge.id}`} item={badge} type="parking" onUnassign={handleUnassignEquipment} consumption={badgeConsumption[badge.id]} />
                  ))}
                </>
              )}
            </div>
          )}
        </AccordionGroup>
      </AccordionContainer>

      {/* Assignment Modals */}
      {showVehicleAssignment && (
        <VehicleAssignmentToDriverModal
          isOpen={showVehicleAssignment}
          onClose={() => setShowVehicleAssignment(false)}
          onSuccess={handleAssignmentSuccess}
          onMessage={onMessage}
          driver={driver}
          vehicles={vehicles}
        />
      )}

      {showOrgTransfer && (
        <DriverOrgTransferModal
          isOpen={showOrgTransfer}
          onClose={() => setShowOrgTransfer(false)}
          onSuccess={() => {
            setShowOrgTransfer(false);
            loadData();
            onUpdate?.();
          }}
          onMessage={onMessage}
          driver={driver}
          organizations={organizations}
        />
      )}

      {showSiteAssignment && (
        <DriverSiteAssignmentModal
          isOpen={showSiteAssignment}
          onClose={() => setShowSiteAssignment(false)}
          onSuccess={async () => {
            // Force parent to refresh driver entity first
            await onUpdate?.();
            // Then reload local data (sites, etc.) after parent has refreshed
            await loadData();
          }}
          onMessage={onMessage}
          driver={driver}
          sites={sites}
        />
      )}

      {showAvailability && (
        <DriverAvailabilityModal
          isOpen={showAvailability}
          onClose={() => setShowAvailability(false)}
          onSuccess={async () => {
            await onUpdate?.();
            await loadData();
          }}
          onMessage={onMessage}
          driver={driver}
        />
      )}

      {showEquipmentAssign && (
        <DriverEquipmentAssignModal
          isOpen={showEquipmentAssign}
          onClose={() => setShowEquipmentAssign(false)}
          onSuccess={async () => {
            setShowEquipmentAssign(false);
            await loadData();
            onUpdate?.();
          }}
          onMessage={onMessage}
          driver={driver}
        />
      )}
    </div>
  );
}

export default DriverOverviewTab;
